import { useState, useCallback } from 'react';
import { useDataCache } from './useDataCache';

interface ProductionOrder {
  id: string;
  orderNumber: string;
  externalKey?: string;
  customerName?: string;
  customerCode?: string;
  branchName?: string;
  productCode: string;
  productName: string;
  quantityPlanned: string;
  unit: string;
  priority: '1' | '2' | '3';
  dueDate?: string;
  plannedStartDate?: string;
  actualStartDate?: string | null;
  actualEndDate?: string | null;
  lotCode?: string;
  routeName?: string;
  machineName?: string;
  notes?: string;
  label?: string;
  designUrl?: string;
  printConfigImage?: number;
  // Matches backend's productionOrderStatusEnum (schema.ts) exactly — was
  // missing COMPLETED/CLOSED, which every status comparison elsewhere in
  // ProductionOrders.tsx already checks against.
  status: 'CREATED' | 'AUTHORIZED' | 'COMPLETED' | 'CLOSED';
  orderType?: 'PRODUCTO_NUEVO' | 'REPETICION' | null;
  createdAt: string;
  updatedAt: string;
  createdByName: string | null;
}

interface ProductionOrderItem {
  id: string;
  itemCode: string;
  itemName: string;
  quantity: string;
  unit: string;
  notes?: string;
}

interface ProductionOrderStatusHistory {
  id: string;
  fromStatus?: 'CREATED' | 'AUTHORIZED';
  toStatus: 'CREATED' | 'AUTHORIZED';
  changedAt: string;
  comment?: string;
  changedByName: string | null;
}

interface ProductionOrderDetail extends ProductionOrder {
  items: ProductionOrderItem[];
  statusHistory: ProductionOrderStatusHistory[];
}

interface ProductionOrdersResponse {
  data: ProductionOrder[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface CreateProductionOrderData {
  orderNumber: string;
  externalKey?: string;
  customerName?: string;
  customerCode?: string;
  branchName?: string;
  productCode: string;
  productName: string;
  quantityPlanned: number;
  unit: string;
  priority?: '1' | '2' | '3';
  dueDate?: string;
  plannedStartDate?: string;
  lotCode?: string;
  routeName?: string;
  machineName?: string;
  notes?: string;
  label?: string;
  designUrl?: string;
  items?: {
    itemCode: string;
    itemName: string;
    quantity: number;
    unit: string;
    notes?: string;
  }[];
}

interface UpdateProductionOrderData extends Partial<CreateProductionOrderData> {}

interface ProductionOrderFilters {
  status?: 'CREATED' | 'AUTHORIZED';
  orderType?: 'PRODUCTO_NUEVO' | 'REPETICION' | 'SIN_DEFINIR';
  priority?: '1' | '2' | '3';
  search?: string;
  customerName?: string;
  branchName?: string;
  productCode?: string;
  orderNumber?: string;
  fromDate?: string;
  toDate?: string;
  page?: number;
  limit?: number;
}

interface ApiIntegrationConfig {
  id: string;
  endpointUrl: string;
  samplePayload?: Record<string, any>;
  updatedAt: string;
  configured: boolean;
}

interface OperationState {
  loading: boolean;
  error: string | null;
}

export function useProductionOrders() {
  const { invalidateCache } = useDataCache();
  
  // Separate loading/error states for different operations
  const [states, setStates] = useState<{
    list: OperationState;
    detail: OperationState;
    create: OperationState;
    update: OperationState;
    delete: OperationState;
    status: OperationState;
    config: OperationState;
    import: OperationState;
  }>({
    list: { loading: false, error: null },
    detail: { loading: false, error: null },
    create: { loading: false, error: null },
    update: { loading: false, error: null },
    delete: { loading: false, error: null },
    status: { loading: false, error: null },
    config: { loading: false, error: null },
    import: { loading: false, error: null },
  });

  // Helper to update specific operation state
  const updateState = useCallback((operation: keyof typeof states, updates: Partial<OperationState>) => {
    setStates(prev => ({
      ...prev,
      [operation]: { ...prev[operation], ...updates }
    }));
  }, []);

  const fetchProductionOrders = useCallback(async (filters: ProductionOrderFilters = {}): Promise<ProductionOrdersResponse> => {
    updateState('list', { loading: true, error: null });
    
    try {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          queryParams.append(key, value.toString());
        }
      });

      const response = await fetch(`/api/production-orders?${queryParams}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al cargar órdenes de producción');
      }

      const result = await response.json();
      updateState('list', { loading: false, error: null });
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error al cargar órdenes de producción';
      updateState('list', { loading: false, error: message });
      throw error;
    }
  }, [updateState]);

  const fetchProductionOrder = useCallback(async (id: string): Promise<ProductionOrderDetail> => {
    updateState('detail', { loading: true, error: null });
    
    try {
      const response = await fetch(`/api/production-orders/${id}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al cargar orden de producción');
      }

      const result = await response.json();
      updateState('detail', { loading: false, error: null });
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error al cargar orden de producción';
      updateState('detail', { loading: false, error: message });
      throw error;
    }
  }, [updateState]);

  const createProductionOrder = useCallback(async (data: CreateProductionOrderData): Promise<ProductionOrder> => {
    updateState('create', { loading: true, error: null });
    
    try {
      const response = await fetch('/api/production-orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al crear orden de producción');
      }

      const result = await response.json();
      updateState('create', { loading: false, error: null });
      invalidateCache(); // Invalidate cache to refresh data
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error al crear orden de producción';
      updateState('create', { loading: false, error: message });
      throw error;
    }
  }, [updateState, invalidateCache]);

  const updateProductionOrder = useCallback(async (id: string, data: UpdateProductionOrderData): Promise<ProductionOrder> => {
    updateState('update', { loading: true, error: null });
    
    try {
      const response = await fetch(`/api/production-orders/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al actualizar orden de producción');
      }

      const result = await response.json();
      updateState('update', { loading: false, error: null });
      invalidateCache(); // Invalidate cache to refresh data
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error al actualizar orden de producción';
      updateState('update', { loading: false, error: message });
      throw error;
    }
  }, [updateState, invalidateCache]);

  const changeOrderStatus = useCallback(async (id: string, status: 'CREATED' | 'AUTHORIZED', comment?: string): Promise<ProductionOrder> => {
    updateState('status', { loading: true, error: null });
    
    try {
      const response = await fetch(`/api/production-orders/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ status, comment }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al cambiar estado de orden');
      }

      const result = await response.json();
      updateState('status', { loading: false, error: null });
      invalidateCache(); // Invalidate cache to refresh data
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error al cambiar estado de orden';
      updateState('status', { loading: false, error: message });
      throw error;
    }
  }, [updateState, invalidateCache]);

  const deleteProductionOrder = useCallback(async (id: string): Promise<void> => {
    updateState('delete', { loading: true, error: null });
    
    try {
      const response = await fetch(`/api/production-orders/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al eliminar orden de producción');
      }

      updateState('delete', { loading: false, error: null });
      invalidateCache(); // Invalidate cache to refresh data
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error al eliminar orden de producción';
      updateState('delete', { loading: false, error: message });
      throw error;
    }
  }, [updateState, invalidateCache]);

  // API Integration functions
  const fetchApiConfig = useCallback(async (): Promise<ApiIntegrationConfig> => {
    updateState('config', { loading: true, error: null });
    
    try {
      const response = await fetch('/api/production-orders/integration/config', {
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al cargar configuración de API');
      }

      const result = await response.json();
      updateState('config', { loading: false, error: null });
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error al cargar configuración de API';
      updateState('config', { loading: false, error: message });
      throw error;
    }
  }, [updateState]);

  const updateApiConfig = useCallback(async (data: {
    endpointUrl: string;
    apiKey: string;
    samplePayload?: Record<string, any>;
  }): Promise<ApiIntegrationConfig> => {
    updateState('config', { loading: true, error: null });
    
    try {
      const response = await fetch('/api/production-orders/integration/config', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al actualizar configuración de API');
      }

      const result = await response.json();
      updateState('config', { loading: false, error: null });
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error al actualizar configuración de API';
      updateState('config', { loading: false, error: message });
      throw error;
    }
  }, [updateState]);

  const importOrders = useCallback(async (orders: CreateProductionOrderData[]): Promise<{
    message: string;
    summary: {
      total: number;
      created: number;
      updated: number;
      skipped: number;
    };
    errors: any[];
  }> => {
    updateState('import', { loading: true, error: null });
    
    try {
      const response = await fetch('/api/production-orders/import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(orders),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al importar órdenes');
      }

      const result = await response.json();
      updateState('import', { loading: false, error: null });
      invalidateCache(); // Invalidate cache to refresh data
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error al importar órdenes';
      updateState('import', { loading: false, error: message });
      throw error;
    }
  }, [updateState, invalidateCache]);

  return {
    // States for each operation
    loading: states,
    
    // Functions
    fetchProductionOrders,
    fetchProductionOrder,
    createProductionOrder,
    updateProductionOrder,
    changeOrderStatus,
    deleteProductionOrder,
    fetchApiConfig,
    updateApiConfig,
    importOrders,
  };
}

export type {
  ProductionOrder,
  ProductionOrderItem,
  ProductionOrderStatusHistory,
  ProductionOrderDetail,
  ProductionOrdersResponse,
  CreateProductionOrderData,
  UpdateProductionOrderData,
  ProductionOrderFilters,
  ApiIntegrationConfig,
};